import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { KanbanCard } from '@/components/kanban/KanbanCard';
import { StageSwatch } from '@/components/StageSwatch';
import { Badge, Button, Checkbox, EmptyState, Skeleton } from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import { formatDecimalString, pluralise } from '@/lib/format';
import {
  columnKeyOf,
  isDealColumn,
  isDropTarget,
  isMixedCurrency,
  visibleCurrencies,
} from '@/lib/kanban-utils';
import type {
  KanbanBoardName,
  KanbanColumn,
  KanbanCurrencyTotal,
  KanbanDateField,
  KanbanQueryParams,
} from '@/lib/types';
import { useColumnVisibility, type ColumnVisibility } from '@/lib/use-column-visibility';
import { useKanban } from '@/lib/use-kanban';

/*
 * The board surface for every kanban board — deals, quotes, invoices,
 * deliverables, companies, and whatever the backend registers next.
 *
 * Everything below is driven by the payload, never by `props.board`: the column
 * list arrives in its final order (server-side, with "Unassigned" pushed last on
 * a NULL `sortOrder`), the move body's key arrives as `columnKey`, the deal
 * pipeline metadata arrives as optional keys the other boards omit outright, and
 * a column with no money says so by sending `totalAmount: null`. Re-deriving any
 * of that from the board name is how the boards drift apart.
 */

/** A card, spelled through the column so the type name stays free for the component. */
type CardOf = KanbanColumn['cards'][number];

/**
 * The stage colour a column carries, read off the datum and never off the board
 * name.
 *
 * `colour` is one of the deals board's `extraColumns`, exactly like
 * `probability` / `isWon` / `isLost`, and the server distinguishes three cases
 * on purpose: **absent** (no colour column at all — quotes, invoices,
 * deliverables), **`null`** (this stage has not been given one), and
 * **`"#rrggbb"`** (a colour chosen in the stage master). All three collapse to
 * the same render — a swatch, or nothing — decided from the payload rather
 * than from `props.board`.
 *
 * Typed `unknown` out, and validated by `StageSwatch`: this is user-editable
 * data from a database row, so nothing between the wire and a `style` may
 * assume it is a colour.
 */
function columnColour(column: KanbanColumn): unknown {
  return (column as KanbanColumn & { colour?: unknown }).colour;
}

/** Private MIME type: a kanban card must not be droppable into a text input. */
const DRAG_MIME = 'application/x-seed-kanban-card';

const SKELETON_COLUMNS = 4;
const SKELETON_CARDS = 3;

/**
 * `meta.dateField` REMAINS AUTHORITATIVE. This only covers the window between a
 * board arriving and its meta doing so, which the contract does not allow but
 * the types do — the moment `meta` exists, the value below is not consulted at
 * all, including when `meta.dateField` is itself `null`.
 *
 * `null` is a first-class entry here, not a missing one. A board whose records
 * carry no meaningful date is a real board: a company has only created/updated,
 * and neither is a pipeline date, so `companies` names no field rather than
 * borrowing another board's. Typing the map `KanbanDateField | null` is what
 * lets it say that instead of being forced to pick one — and keeps the map
 * total, so a sixth board is a compile error here rather than an `undefined`
 * indexed into `KANBAN_DATE_LABEL` at render.
 */
const FALLBACK_DATE_FIELD: Record<KanbanBoardName, KanbanDateField | null> = {
  deals: 'closingDate',
  quotes: 'validTill',
  invoices: 'dueDate',
  deliverables: 'endDate',
  companies: null,
};

/**
 * Wide enough for the roomier card, capped so one column still fits 320px.
 *
 * 21rem is the desktop figure: the card's 16px title and wrapping company name
 * broke to a third and fourth line at a narrower width, on the institution
 * names this CRM actually holds.
 *
 * `max-w` is a cap, not a second breakpoint: `shrink-0` stops the flex row
 * squeezing columns to fit — the board scrolls sideways instead — but
 * `max-width` still wins over `width`, so at 320px the column resolves to
 * 280px inside the page's own 16px gutters. Nothing is clipped and the page
 * itself never scrolls sideways.
 *
 * One constant for the real columns and for the skeleton, so a loading board is
 * the same shape as the board that replaces it.
 */
const COLUMN_WIDTH = 'w-[21rem] max-w-[calc(100vw-2.5rem)] shrink-0';

/**
 * The column shell: one hairline boundary, one scroll container.
 *
 * The header is a flex sibling of the card list rather than a `sticky` child of
 * it, which reaches the same place more cheaply — the list is the only thing
 * that scrolls, so the header cannot move. Vertical scroll lives per column on
 * purpose: a 76-card column must not push the other four off the bottom of the
 * page and make the board itself unreadable.
 */
const COLUMN_SHELL = 'flex max-h-[70vh] flex-col overflow-hidden rounded-lg border bg-surface-sunken';

/**
 * The board's "create" affordance — supplied by the page, never built here.
 *
 * The board stays ignorant of what it is a board OF: it does not import a form,
 * does not know the entity's fields, and does not decide who may create one. It
 * is handed a label, a way to ask the page to open the page's OWN modal, and a
 * number to watch. Everything board-specific stays on the page that already
 * owns the modal for its table view, so there is still exactly one "is the
 * create form open" in the app per entity.
 *
 * **Absence is the gate.** A page that may not create passes nothing and no
 * control renders — a viewer never sees a button that would 403. There is
 * deliberately no `canCreate` prop to get wrong at one call site out of four.
 */
export interface KanbanCreateAction {
  /** Same wording as the page header's button — "New deal", "New quote". */
  label: string;
  /** Opens the page's existing create modal. */
  onCreate: () => void;
  /**
   * Bumped by the page after a successful save; the board then refetches ONCE.
   *
   * The value itself is meaningless — only a change matters — because the board
   * cannot see the modal and the modal cannot see the board. A move response is
   * one card and never triggers a reload (see `use-kanban.ts`); a create has no
   * response the board can splice in at all, since it does not know which
   * column the new record landed in or what its card looks like. One request
   * per create is the honest price, and it is a fraction of the per-drag
   * refetch that hook exists to avoid.
   */
  refreshToken: number;
}

export interface KanbanBoardProps {
  board: KanbanBoardName;
  params: KanbanQueryParams;
  /** Detail-page route for a record. */
  hrefFor: (cardId: number) => string;
  /** False for a viewer — the move endpoint requires a write role. */
  canMove: boolean;
  /** False while the table view is showing, so the board fetches nothing. */
  enabled: boolean;
  /** Omit entirely when the signed-in user may not create — see the type. */
  create?: KanbanCreateAction;
  /**
   * Rendered inside each card. Omit and cards render exactly as today.
   *
   * The per-card equivalent of `create`: the page hands in the same "create a
   * document from this record" control its table rows carry, and this file
   * neither builds it nor asks which board it is on. Nothing here reads the
   * card beyond passing it back — the page already knows what entity it is
   * showing and what may be raised from one of its records.
   *
   * **Absence is the gate**, as with `create`: a page whose user may not create
   * passes nothing and no card shows a control, so there is no `canCreate` flag
   * to get wrong at one call site out of five.
   */
  cardAction?: (card: CardOf) => ReactNode;
  /** How many controls `cardAction` renders (default 1); see KanbanCard. */
  cardActionCount?: number;
  /**
   * Asked before every move (drag or Move menu), ahead of the optimistic
   * update. Omit and every move proceeds as before.
   *
   * - `'proceed'`: the board moves the card itself, as usual.
   * - `'cancel'`: nothing is sent and the card stays where it was.
   * - `'handled'`: the page saved the change itself, so the board reloads.
   *
   * The page decides from the column's data (e.g. a deal stage's `isLost`
   * flag); this file never asks which board it is on.
   */
  confirmMove?: (card: CardOf, toColumn: KanbanColumn) => Promise<'proceed' | 'cancel' | 'handled'>;
  /*
   * ⚠️ No board-specific logic belongs in this component. A board-specific fact
   * arrives IN THE DATA (`data.columnKey`, `meta.moveEndpoint`, `totalAmount:
   * null` on the money-less board) or is handed in by the page as a render
   * prop, the way `create` and `cardAction` are. Never add an
   * `if (board === 'quotes')` here — that rule is what has kept deals, quotes,
   * invoices, deliverables and companies from drifting apart.
   */
}

interface DragState {
  cardId: number;
  /** Null when the card is being dragged out of "Unassigned". */
  fromColumnId: number | null;
}

export function KanbanBoard({
  board: boardName,
  params,
  hrefFor,
  canMove,
  enabled,
  create,
  cardAction,
  cardActionCount,
  confirmMove,
}: KanbanBoardProps): ReactElement {
  const {
    board,
    meta,
    loading,
    refetching,
    error,
    moveError,
    dismissMoveError,
    movingCardId,
    moveCard: rawMoveCard,
    reload,
  } = useKanban(boardName, params, enabled);

  /*
   * The page's `confirmMove` runs BEFORE the hook's optimistic move, so a
   * cancelled move never shows the card jumping across and back. Both routes
   * (drop and the Move menu) go through here. Without `confirmMove`, or when
   * the card or column cannot be found on the board, the hook's own move runs
   * untouched and reports its own errors.
   */
  const moveCard = useCallback(
    async (cardId: number, fromColumnId: number | null, toColumnId: number) => {
      if (confirmMove) {
        const current = board;
        const fromColumn = current?.columns.find((column) => column.id === fromColumnId);
        const card = fromColumn?.cards.find((entry) => entry.id === cardId);
        const toColumn = current?.columns.find((column) => column.id === toColumnId);
        if (card && toColumn && fromColumnId !== toColumnId) {
          let decision: 'proceed' | 'cancel' | 'handled';
          try {
            decision = await confirmMove(card, toColumn);
          } catch {
            decision = 'cancel';
          }
          if (decision === 'cancel') return;
          if (decision === 'handled') {
            reload();
            return;
          }
        }
      }
      await rawMoveCard(cardId, fromColumnId, toColumnId);
    },
    [board, confirmMove, rawMoveCard, reload],
  );

  /*
   * `dataTransfer.getData()` is deliberately unreadable during `dragover` — the
   * spec only exposes the payload on `drop` — so the source column has to live
   * in React state as well, or no column can tell whether it is a legal target
   * while the pointer is actually over it.
   */
  const [drag, setDrag] = useState<DragState | null>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  /*
   * A create landed on the page, so the board is now missing a card.
   *
   * The token is COMPARED, never used: the first render seeds the ref with
   * whatever the page happens to be holding, so mounting the board — including
   * remounting it after a create made while the table view was showing — never
   * spends a second request on top of the fetch the hook is already making.
   * Only a change from the value this board has already seen reloads, which is
   * once per successful save and never on a plain re-render.
   */
  const refreshToken = create?.refreshToken;
  const seenRefreshRef = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken === seenRefreshRef.current) return;
    seenRefreshRef.current = refreshToken;
    reload();
  }, [refreshToken, reload]);

  // Memoised on `board` alone: `?? []` would otherwise mint a fresh array on
  // every render and re-run everything derived from it.
  const columns = useMemo(() => board?.columns ?? [], [board]);

  /*
   * HIDING A COLUMN IS PRESENTATION ONLY, and every consequence of that is
   * deliberate:
   *
   * - it does not touch `params`, so the request is byte-for-byte the same one
   *   and nothing is filtered out server-side;
   * - it does not touch `meta.totalRecords`, which stays the whole filtered
   *   record count for the board — the toolbar shows that figure, and a hidden
   *   column's records are still inside it;
   * - it does not touch any column's own `count` or `totalAmount`, which are
   *   the server's numbers for the whole column either way.
   *
   * A board silently missing a stage is indistinguishable from a broken board —
   * this repo has already been bitten by a persisted preference that made
   * working pages look missing — so the state is stated permanently on the
   * trigger ("2 hidden") and in the toolbar, never only inside the panel that
   * sets it.
   */
  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const visibility = useColumnVisibility(boardName, columnIds);
  const { isVisible, showAll, hiddenCount, hasHidden } = visibility;
  const visibleColumns = useMemo(
    // Filtered, never reordered — see the render comment below.
    () => columns.filter((column) => isVisible(column.id)),
    [columns, isVisible],
  );

  /*
   * Native HTML5 drag and drop does not fire on touch at all, so every move has
   * to be reachable from the card's own "Move" menu too. Both routes end in the
   * same `moveCard`. Only real drop targets qualify: a retired column 422s, and
   * "no stage" is not expressible in a body that requires a positive integer.
   *
   * Built from ALL columns, hidden ones included. "Move it to a stage I am not
   * currently looking at" is legitimate, and the alternative fails in the
   * obvious case: hide four of five columns and every card left on screen
   * becomes unmovable. Hiding changes what is *rendered*, so it removes the
   * drop target by construction — an unrendered column cannot be dragged onto —
   * but it must never remove the move.
   */
  const moveTargets = useMemo(
    () =>
      columns.flatMap((column) =>
        isDropTarget(column) && column.id !== null ? [{ id: column.id, name: column.name }] : [],
      ),
    [columns],
  );

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLLIElement>, card: CardOf, fromColumnId: number | null) => {
      if (!canMove) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(DRAG_MIME, String(card.id));
      event.dataTransfer.setData('text/plain', card.title ?? `#${card.id}`);
      setDrag({ cardId: card.id, fromColumnId });
    },
    [canMove],
  );

  const handleDragEnd = useCallback(() => {
    setDrag(null);
    setHoverKey(null);
  }, []);

  /** True only where a drop would actually be sent to the server. */
  const acceptsDrag = useCallback(
    (column: KanbanColumn) =>
      canMove &&
      drag !== null &&
      isDropTarget(column) &&
      column.id !== null &&
      column.id !== drag.fromColumnId,
    [canMove, drag],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLLIElement>, column: KanbanColumn) => {
      /*
       * No `preventDefault()` on a column that cannot take the card: the drop
       * is then refused by the browser and the cursor says so, which is the
       * honest answer for a retired column and for "Unassigned". The user never
       * gets as far as a request the server would reject.
       */
      if (!acceptsDrag(column)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setHoverKey(columnKeyOf(column));
    },
    [acceptsDrag],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLLIElement>, column: KanbanColumn) => {
    // Ignore the leave events fired while crossing the column's own children.
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setHoverKey((current) => (current === columnKeyOf(column) ? null : current));
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLLIElement>, column: KanbanColumn) => {
      event.preventDefault();
      setHoverKey(null);
      const source = drag;
      setDrag(null);
      if (source === null || column.id === null) return;
      if (!canMove || !isDropTarget(column) || column.id === source.fromColumnId) return;
      void moveCard(source.cardId, source.fromColumnId, column.id);
    },
    [canMove, drag, moveCard],
  );

  if (!enabled && board === null) return <></>;

  /*
   * `??` would be wrong here: `meta.dateField` is legitimately `null` on a
   * dateless board, and coalescing would send that straight past the
   * authoritative answer into the fallback. Presence of `meta` is the test.
   */
  const dateField: KanbanDateField | null =
    meta !== null ? meta.dateField : FALLBACK_DATE_FIELD[boardName];
  // Deals never carry a reference; quotes and invoices are known by theirs.
  const showReference = (board?.board ?? boardName) !== 'deals';
  const empty = board !== null && (columns.length === 0 || (meta?.totalRecords ?? 0) === 0);
  const showToolbar = board !== null && !empty && columns.length > 0;

  return (
    <div className="grid min-w-0 gap-3">
      {error && (
        <Notice
          tone="danger"
          role="alert"
          title={<>Couldn&rsquo;t load the {boardName} board.</>}
          action={
            <Button variant="ghost" size="sm" onClick={reload}>
              Retry
            </Button>
          }
        >
          <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      {/*
       * A failed move concerns one card, not the board — it must never blank out
       * the other nineteen. A dismissible banner rather than a toast: the card
       * is still sitting where the server left it, and the explanation has to
       * survive long enough to be read next to it.
       */}
      {moveError && (
        <Notice
          tone="danger"
          role="alert"
          title={<>Couldn&rsquo;t move that card.</>}
          action={
            <Button variant="ghost" size="sm" onClick={dismissMoveError}>
              Dismiss
            </Button>
          }
        >
          <span className="min-w-0 [overflow-wrap:anywhere]">{moveError}</span>
        </Notice>
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {movingCardId !== null ? 'Moving card…' : refetching ? 'Updating board…' : ''}
      </p>

      {showToolbar && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {/*
           * ⛔ **THE TOOLBAR COUNT LINE WAS REMOVED — owner's helper-text ruling,
           * 2026-09-05.** It read `{visible} of {total} columns shown` (or a bare
           * column count when nothing was hidden) followed by `{N} records on the
           * board`.
           *
           * ⚠️ **BOTH HALVES SAID SOMETHING THE BOARD CANNOT SHOW ON ITS OWN, and
           * both are now unsaid:**
           *
           * - that columns are HIDDEN at all. The "Show all" button beside this
           *   still appears only when something is hidden, so that button is now
           *   the only signal, and the badge on the columns menu trigger.
           * - that `meta.totalRecords` is BOARD-WIDE and is NOT reduced by
           *   hiding. A hidden column's records are still counted in it, which is
           *   exactly why both numbers used to be said in one line — separated,
           *   the record count reads as a total of what is visible.
           *
           * **Do not reinstate only the record count.** On its own it is the more
           * misleading of the two.
           */}
          <div className="min-w-0" />

          <div className="flex shrink-0 items-center gap-1.5">
            {hasHidden && (
              <Button variant="ghost" size="sm" onClick={showAll}>
                Show all
              </Button>
            )}
            <ColumnsMenu columns={columns} visibility={visibility} />
          </div>
        </div>
      )}

      {loading && board === null ? (
        <BoardSkeleton />
      ) : empty ? (
        <EmptyState
          title={`Nothing on the ${boardName} board`}
          description="No records match the current filters."
        />
      ) : board === null ? (
        <></>
      ) : visibleColumns.length === 0 ? (
        /*
         * Every column hidden is a legal state of the toggle, and it has to read
         * as a choice rather than as an empty board: the records are all still
         * there, and still counted in the toolbar above.
         */
        <EmptyState
          title={`${pluralise(hiddenCount, 'column')} hidden`}
          description="Nothing is filtered out — the records are all still here."
          action={
            <Button variant="secondary" size="sm" onClick={showAll}>
              Show all columns
            </Button>
          }
        />
      ) : (
        /*
         * `min-w-0` is load-bearing, not defensive. A flex or grid child
         * defaults to `min-width: auto`, so without it this container refuses to
         * shrink below its widest column, widens its own track, and takes the
         * whole page sideways below 768px — the exact regression a wide table
         * caused on the detail page. The board scrolls; the page never does.
         */
        <div
          aria-busy={refetching || undefined}
          className={cx(
            'min-w-0 overflow-x-auto pb-1 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            refetching && 'opacity-60',
          )}
        >
          <ol className="m-0 flex list-none items-start gap-3 p-0">
            {/*
             * Rendered in the order received. Never sorted by `sortOrder`:
             * "Unassigned" carries null there, the server has already placed it
             * last, and a comparator would both promote it and have to invent
             * an answer for the null. Hiding only removes entries from this
             * list; the ones that remain keep the server's order.
             */}
            {visibleColumns.map((column) => (
              <BoardColumn
                key={columnKeyOf(column)}
                column={column}
                columnKey={board.columnKey}
                listPath={`/${board.board}`}
                dateField={dateField}
                showReference={showReference}
                hrefFor={hrefFor}
                canMove={canMove}
                movingCardId={movingCardId}
                moveTargets={moveTargets}
                cardAction={cardAction}
                {...(cardActionCount === undefined ? {} : { cardActionCount })}
                onMove={moveCard}
                dropAllowed={acceptsDrag(column)}
                dragActive={drag !== null}
                hovered={hoverKey === columnKeyOf(column)}
                onCardDragStart={handleDragStart}
                onCardDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              />
            ))}
          </ol>
        </div>
      )}

      {/*
       * The create control sits at the foot of the board, outside the
       * horizontal scroller, so it stays reachable at 320px without swiping
       * through every column first.
       *
       * Board-level, not per-column: a button in a column header would imply
       * creating from "Negotiation" files the record into Negotiation, which
       * this cannot do — `columnKey` names a write key on the entity, not a
       * form field.
       *
       * Rendered in every state, including the empty board and the first
       * load — exactly when someone wants to create the first record.
       *
       * ⚠️ **`primary`, matching the page header's own button — owner's
       * instruction, 11 Sep 2026.** *SUPERSEDED: `variant="secondary" size="sm"`.*
       * The two buttons open the same modal and say the same words, so a
       * secondary one below the board read as a lesser, different control
       * rather than as the same action brought within reach. `accent-brand` is
       * rationed to primary actions, and creating a record is the primary
       * action of a board — this is the one place on the board that qualifies.
       *
       * ⚠️ **The default height, not `sm`.** It is the only control below the
       * scroller, so nothing sits beside it to be sized against, and the whole
       * point of the row is that somebody arriving at an empty or fully
       * scrolled board can find it. The `max-sm:h-11` touch bump inside
       * `Button` comes with the default height and is wanted here.
       *
       * 🔴 **REMOVED — UI rework, owner decision 6 (2026-10-11): one primary per
       * screen region.** *SUPERSEDED, everything above about rendering it.* It
       * opened the same modal with the same words as the page header's button,
       * so it was a visual duplicate of a primary already on screen, and it
       * carried no column (board-level, not per-stage). The page header's button
       * is the create control in board view as in table view. `create` stays on
       * the props: `refreshToken` is still what reloads the board after a save.
       */}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The "Columns" control
 * ------------------------------------------------------------------ */

/**
 * A real popover: Escape closes it and returns focus, a pointer-down outside
 * closes it, and the trigger carries `aria-haspopup` / `aria-expanded`.
 *
 * Not portalled, unlike `ColumnFilter`. It hangs off a toolbar that sits
 * *outside* the board's horizontal scroll container, so an absolutely
 * positioned panel neither clips nor drifts, and keeping it in the DOM next to
 * its trigger means the tab order is already right and no focus trap is needed.
 */
function ColumnsMenu({
  columns,
  visibility,
}: {
  columns: KanbanColumn[];
  visibility: ColumnVisibility;
}) {
  const { isVisible, toggle, showAll, hiddenCount, hasHidden } = visibility;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(event) => {
        if (!open || event.key !== 'Escape') return;
        event.preventDefault();
        // Otherwise a board rendered inside a Modal closes the Modal as well.
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }}
    >
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        leadingIcon={<ColumnsIcon />}
        trailingIcon={<ChevronIcon open={open} />}
      >
        Columns
        {/*
         * The badge is the persistent half of the feature. Worded, not merely
         * tinted — a warning tone with no text would leave "why is this board
         * missing a stage" unanswerable at a glance.
         */}
        {hasHidden && <Badge tone="warning">{hiddenCount} hidden</Badge>}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-labelledby={titleId}
          /*
           * ⚠️ `z-20`, NOT `z-30`. 30 is the APP-CHROME rung — the sticky top
           * bar and the sidebar rail — and this is page content. At 30 it tied
           * with the bar and, being later in the document, painted over the
           * account menu whenever the board toolbar scrolled under it. That is
           * the same tie that put the pinned table header over the same menu
           * across the whole app; see the ladder recorded in `Table.tsx` and
           * `AppShell.tsx`.
           *
           * Page content stays at or below 20. An equal z-index is settled by
           * document order, so "same rung as the chrome" always means the
           * chrome loses.
           */
          className="absolute right-0 z-20 mt-1 flex w-64 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
        >
          <div className="border-b border-border px-3 py-2">
            {/*
             * ⛔ **`This view only. Nothing is filtered out and counts stay
             * whole.` was removed here — owner's helper-text ruling, 2026-09-05.**
             *
             * ⚠️ **Both claims remain TRUE and are now unstated: hiding is
             * PRESENTATION ONLY** — it never changes what is fetched, and the
             * board-wide total is unaffected — **and a hidden column's cards are
             * still reachable**, because hidden columns still appear in a card's
             * Move menu. Unticking a column now looks like filtering records
             * away. Do not make it actually filter.
             */}
            <p id={titleId} className="m-0 text-xs font-semibold text-fg">
              Show columns
            </p>
          </div>

          {/* Payload order here too — the panel must read like the board. */}
          <ul className="m-0 max-h-64 list-none overflow-y-auto p-1">
            {/*
             * ⛔ **THE PER-COLUMN `description` WAS REMOVED — owner's helper-text
             * ruling, 2026-09-05.** It read `{N} cards`, joined with `Retired`
             * for an inactive column and `No stage` for the `id: null` one.
             *
             * ⚠️ **The two STATES are still badged on the column header itself
             * ("Retired", "No stage"), so they are not invisible — but the drag
             * phrase that used to sit beside those badges went in the same pass,
             * so what either state means for DROPPING is now unsaid in both
             * places.** A retired column is drag-OUT only and the `id: null`
             * column is inexpressible as a drop target, and the card count that
             * used to sit here is gone too.
             *
             * `columnKeyOf` still keys the row; `column.isActive` and
             * `column.id === null` are still read by the drop-target logic.
             */}
            {columns.map((column) => (
              <li key={columnKeyOf(column)}>
                <Checkbox
                  className="rounded-md px-2 py-1.5 transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none"
                  checked={isVisible(column.id)}
                  onChange={() => toggle(column.id)}
                  label={<span className="text-xs font-medium text-fg">{column.name}</span>}
                />
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-2 border-t border-border px-2 py-1.5">
            {/*
             * The counter stays where it is, and it is what makes the button
             * beside it legible while disabled: `All shown` IS the reason the
             * control would do nothing, already on screen and already
             * adjacent. That is why no `title` is routed to the button — the
             * why-this-is-disabled sentence exists, it simply did not have to
             * be written a second time.
             *
             * ⚠️ It reads `hiddenCount`, which the hook counts as the
             * intersection with the CURRENT board rather than as the stored
             * set's size. Two consequences worth keeping: pressing the button
             * beside it always leaves this reading `All shown`, and a stored
             * id for a column this board no longer has can never make it
             * claim a hidden column that is not there.
             */}
            <span className="tabular pl-1 text-2xs text-fg-muted">
              {hasHidden ? `${hiddenCount} hidden` : 'All shown'}
            </span>
            {/*
             * ⚠️ **`Select all` — owner's instruction, 2026-09-13: every
             * multi-tick control in the app offers one.** *SUPERSEDED, the
             * label alone: `Show all`.*
             *
             * ⛔ **THE CONTROL WAS RENAMED, NOT ADDED. Do not add a second
             * one.** The capability has always been here and was already the
             * right shape — a `Button`, **disabled rather than hidden** when
             * it would do nothing, which is exactly the `Select all` in
             * `ColumnFilter`. What this bank lacked was the app's word for
             * it, so a reader who knows `Select all` from every other tick
             * bank did not recognise it here. A second button beside this one
             * would have been two controls for one action — the failure this
             * repo already records against a nav placeholder that grows a
             * second row instead of gaining its own `to`.
             *
             * ⚠️ **It CLEARS THE HIDDEN SET; there is no visible set to
             * write.** `showAll()` is the hook's own verb, and the hook stores
             * the HIDDEN ids deliberately: storing the visible ones would make
             * every stage added later default to hidden and silently vanish
             * from the board of everyone who ever touched this control. A
             * select-all that assembled a list of visible ids would be a
             * second representation of the same fact, and the wrong one.
             *
             * ⚠️ **`ColumnFilter`'s pair acts on its *visible* subset — the
             * options surviving its search box. There is no search box here**,
             * so that subset is always every column and the distinction cannot
             * arise. Add one, and this has to narrow with it.
             *
             * ⛔ **ITS OPPOSITE IS DELIBERATELY ABSENT, and that is not an
             * omission for a later pass to finish.** `ColumnFilter` pairs
             * `Select all` with `Clear`; a `Clear` here would promise to untick
             * every box, and the hook REFUSES to hide the last visible column,
             * because a blank board reads as broken rather than as a choice.
             * So the control could not perform its own verb — on every board,
             * every time. Withholding it is the same ruling this app applies
             * to the Xero `sync` affordance: an action whose only outcome is a
             * refusal is not shipped, rather than shipped and stopping short.
             *
             * ⚠️ **This is CHROME, not a filter, and must never be worded as
             * one.** Ticking a column back on fetches nothing and changes no
             * count: hiding is presentation only, the board-wide total is
             * unaffected, and a hidden column's cards stay reachable through a
             * card's Move menu. The `Show all` controls on the toolbar and on
             * the every-column-hidden empty state keep their own wording on
             * purpose — they are recovery affordances in a context with no
             * ticks in it, where the consequence is the useful word.
             */}
            <Button variant="ghost" size="sm" onClick={showAll} disabled={!hasHidden}>
              Select all
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** Three panes: the board's own shape, not a generic filter funnel. */
function ColumnsIcon() {
  return (
    <svg {...stroke} className="h-3.5 w-3.5 shrink-0">
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="16" rx="1" />
      <rect x="17" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      {...stroke}
      className={cx(
        'h-3 w-3 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * A column
 * ------------------------------------------------------------------ */

interface BoardColumnProps {
  column: KanbanColumn;
  /** `stageId` or `statusId`, straight off the payload — never hardcoded. */
  columnKey: string;
  listPath: string;
  /** Null on a board whose records carry no date — the card then shows none. */
  dateField: KanbanDateField | null;
  showReference: boolean;
  hrefFor: (cardId: number) => string;
  canMove: boolean;
  movingCardId: number | null;
  moveTargets: { id: number; name: string }[];
  /** Straight through to the card. Undefined on a page that supplies none. */
  cardAction?: (card: CardOf) => ReactNode;
  cardActionCount?: number;
  onMove: (cardId: number, fromColumnId: number | null, toColumnId: number) => Promise<void>;
  dropAllowed: boolean;
  dragActive: boolean;
  hovered: boolean;
  onCardDragStart: (
    event: DragEvent<HTMLLIElement>,
    card: CardOf,
    fromColumnId: number | null,
  ) => void;
  onCardDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLLIElement>, column: KanbanColumn) => void;
  onDragLeave: (event: DragEvent<HTMLLIElement>, column: KanbanColumn) => void;
  onDrop: (event: DragEvent<HTMLLIElement>, column: KanbanColumn) => void;
}

function BoardColumn({
  column,
  columnKey,
  listPath,
  dateField,
  showReference,
  hrefFor,
  canMove,
  movingCardId,
  moveTargets,
  cardAction,
  cardActionCount,
  onMove,
  dropAllowed,
  dragActive,
  hovered,
  onCardDragStart,
  onCardDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: BoardColumnProps) {
  const unassigned = column.id === null;
  const retired = !column.isActive && !unassigned;
  // Detected off the metadata, not off the board name: quotes and invoices omit
  // these keys entirely, and a fourth board would too.
  const deal = isDealColumn(column) ? column : null;
  const won = deal?.isWon === true;
  const lost = deal?.isLost === true;
  const droppable = isDropTarget(column);
  // Won, lost, retired and "no stage" are the only states worth a badge. A
  // normal column shows none of them, which is what lets the exceptional ones
  // actually draw the eye.
  const flagged = won || lost || retired || unassigned;

  const codes = visibleCurrencies(column);
  const mixed = isMixedCurrency(column);
  const soleCode = codes.length === 1 ? codes[0] : undefined;

  /*
   * A column with no money at all.
   *
   * `totalAmount: null` is the server saying this board does not carry a value
   * — a deliverable is a unit of work, and what it is worth lives on the quote
   * or invoice raised against it. That is NOT a total of zero, so there is no
   * "0.00" to print and no currency chip to hang beside it; the header simply
   * has no money line, and line two disappears with it unless the column has
   * pipeline metadata to put there.
   *
   * Read off the datum, exactly as `isDealColumn` reads off `probability`
   * rather than off `board === 'deals'`. Nothing here knows the word
   * "deliverables", so a fifth money-less board renders correctly untouched.
   */
  const total = column.totalAmount;
  /*
   * The per-currency breakdown (2026-10-15) wins whenever the API sends it:
   * `null` is the same "no money on this board" as `totalAmount: null`, `[]` is
   * an empty money column, and only an ABSENT key — an older API — falls back to
   * the deprecated cross-currency `totalAmount`.
   */
  const byCurrency = column.totalsByCurrency;
  const showMoney = byCurrency === undefined ? total !== null : byCurrency !== null;

  /*
   * `hasMore` is a flag, not a cursor. There is no per-column paging endpoint —
   * `cardsPerColumn` is board-wide and capped at 100, and `?page=2` silently
   * returns page 1 with a 200 — so the honest affordance is a link out to the
   * list filtered to this column, not an infinite scroller that would quietly
   * re-append the first page for ever.
   *
   * `view=table` IS THE POINT OF THE LINK and must stay explicit. Every list
   * page now opens on its board, and the bare URL means board — so without
   * this the link resolves to the board the reader is already looking at,
   * filtered to the one column they are already in. The truncated cards it
   * exists to reach would stay exactly as unreachable, and the control would
   * look like it worked.
   */
  const viewAllHref =
    column.id === null ? null : `${listPath}?view=table&${columnKey}=${column.id}`;

  const targetsForCard = useMemo(
    () => moveTargets.filter((target) => target.id !== column.id),
    [moveTargets, column.id],
  );

  const countLabel = pluralise(column.count, 'card');

  /*
   * THE CARD A PRESS HAS TAKEN OUT OF THE DRAG, and the reason it has to exist.
   *
   * `draggable` and `onDragStart` are on the `<li>`, not on anything inside it.
   * Per the HTML drag spec the drag source is the nearest draggable ANCESTOR,
   * so a `draggable={false}` child is still dragged as part of the card, and
   * `dragstart` fires at the `<li>` — meaning a guard on the child is never
   * even reached. Press a menu trigger inside a card, twitch the pointer, and
   * the CARD starts moving; the drag then suppresses the click, so the menu
   * never opens either. Nothing throws and nothing looks wrong.
   *
   * So the suppression has to happen where `draggable` lives. A pointerdown
   * inside an element marked `data-kanban-no-drag` takes the attribute off this
   * card until the pointer is released — Chrome reads `draggable` when the
   * movement threshold is crossed, which is always after the pointerdown that
   * cleared it.
   *
   * ONE id, not a set: there is one primary pointer, so at most one card can be
   * held at a time. And it is only wired up when the page actually supplies an
   * action — with no `cardAction` the handlers are not attached, the id stays
   * null, and `draggable` resolves to exactly what it did before this existed.
   */
  const [noDragCardId, setNoDragCardId] = useState<number | null>(null);
  const suppressible = cardAction !== undefined;

  return (
    <li
      /*
       * A retired column and "Unassigned" take no drop: the first is 422'd
       * server-side, the second is inexpressible in a move body that requires a
       * positive integer. Dragging OUT of either is normal — it is how they
       * drain — so the handlers stay attached and `acceptsDrag` does the work.
       */
      onDragOver={(event) => onDragOver(event, column)}
      onDragLeave={(event) => onDragLeave(event, column)}
      onDrop={(event) => onDrop(event, column)}
      className={cx(
        COLUMN_WIDTH,
        COLUMN_SHELL,
        'transition-colors duration-150 ease-out motion-reduce:transition-none',
        hovered && dropAllowed
          ? 'border-focus-ring bg-surface-active ring-2 ring-focus-ring'
          : dragActive && dropAllowed
            ? 'border-dashed border-hairline-strong'
            : 'border-border',
        retired && 'border-dashed opacity-75',
      )}
    >
      <section aria-label={`${column.name}, ${countLabel}`} className="flex min-h-0 flex-1 flex-col">
        <header
          className={cx(
            'grid gap-1 border-b border-border bg-surface-sunken px-2.5 py-2',
            // Won and lost are marked by a solid rule AND a worded badge below,
            // never by hue alone.
            won && 'border-l-2 border-l-success',
            lost && 'border-l-2 border-l-danger',
          )}
        >
          {/*
           * Line one is the identity of the column and nothing else: the name,
           * and the count as a figure the eye can land on. `column.count` is the
           * whole filtered column, not `cards.length` — the footer below is the
           * only place those two are allowed to differ in public.
           */}
          <div className="flex items-baseline gap-2">
            <h3
              className="m-0 flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold text-fg"
              title={column.name}
            >
              {/*
               * The stage's own colour, when the board sends one. It is a
               * decoration on a name that already says everything: the swatch
               * is `aria-hidden`, the heading text is unchanged, and a reader
               * who cannot see the colour — or a column whose stage has none —
               * loses nothing. An invalid or missing value renders no square
               * rather than a grey one, which would claim a colour the stage
               * has not been given.
               */}
              <StageSwatch colour={columnColour(column)} />
              <span className="truncate">{column.name}</span>
            </h3>
            <span
              className="tabular shrink-0 rounded-sm border border-border bg-surface px-1.5 py-0.5 text-2xs font-semibold text-fg-secondary"
              title={`${countLabel} in this column`}
            >
              {column.count}
            </span>
          </div>

          {/*
           * Line two is the money, with the stage probability demoted beside
           * it — and it is omitted outright when the column has neither. An
           * always-rendered row would leave a gap of dead padding under the
           * name on a money-less board, which reads as a figure that failed to
           * load rather than as a board that has no figure; name + count with
           * nothing under it reads as the whole header, because it is.
           */}
          {(showMoney || deal !== null) && (
            <div className="flex items-start gap-2">
              {byCurrency !== undefined && byCurrency !== null ? (
                <div className="min-w-0 flex-1">
                  <CurrencyTotals totals={byCurrency} />
                </div>
              ) : (
                byCurrency === undefined &&
                total !== null && (
                  <div className="min-w-0 flex-1">
                    <ColumnTotal
                      totalAmount={total}
                      mixed={mixed}
                      codes={codes}
                      soleCode={soleCode}
                      partial={column.hasMore}
                    />
                  </div>
                )
              )}
              {deal !== null && (
                <span
                  className={cx(
                    'tabular shrink-0 text-2xs text-fg-subtle',
                    // Without the money element to push against, the percentage
                    // has to hold the right edge itself.
                    !showMoney && 'ml-auto',
                  )}
                  title="Stage probability"
                >
                  {deal.probability}%<span className="sr-only"> stage probability</span>
                </span>
              )}
            </div>
          )}

          {/* Line three exists only on an exceptional column. */}
          {flagged && (
            <div className="flex flex-wrap items-center gap-1">
              {won && (
                <Badge tone="success" dot>
                  Won
                </Badge>
              )}
              {lost && (
                <Badge tone="danger" dot>
                  Lost
                </Badge>
              )}
              {retired && (
                <Badge tone="warning" dot>
                  Retired
                </Badge>
              )}
              {unassigned && <Badge tone="neutral">No stage</Badge>}
              {/*
               * ⛔ **The drag phrase beside these badges was removed — owner's
               * helper-text ruling, 2026-09-05.** It read `moves out only` on a
               * retired column and `move a card out to assign one` on the
               * `id: null` one.
               *
               * ⚠️ **The BADGES stay — they are state labels, not prose — but
               * neither now says what it means for DRAGGING, and the behaviour is
               * unchanged:** a retired column is drag-OUT only (a move INTO it is
               * refused server-side), and "No stage" is inexpressible as a drop
               * target because the move body requires a positive integer. **A
               * card that will not land in either now reads as a broken board.**
               */}
            </div>
          )}
        </header>

        {column.cards.length === 0 ? (
          droppable ? (
            /*
             * An active column with nothing in it is still somewhere a card can
             * land, so it has to look like a target rather than like dead space
             * between two columns.
             */
            <div
              className={cx(
                'm-2.5 grid min-h-28 place-items-center rounded-lg border border-dashed px-3 text-center transition-colors duration-150 ease-out motion-reduce:transition-none',
                hovered && dropAllowed
                  ? 'border-focus-ring bg-surface-active text-fg'
                  : 'border-hairline-strong text-fg-subtle',
              )}
            >
              <p className="m-0 text-2xs">
                {dragActive && dropAllowed ? 'Drop here' : 'Nothing here yet'}
              </p>
            </div>
          ) : (
            // No dashed outline on a column that cannot be dropped into — the
            // outline is the affordance, and offering it here would be a lie.
            <p className="m-0 px-2.5 py-6 text-center text-2xs text-fg-subtle">Nothing here</p>
          )
        ) : (
          <ul
            aria-label={`${column.name} cards`}
            // A little more air between cards than before: the cards are taller
            // now, and a 6px gutter between two 140px boxes reads as one slab.
            className="m-0 min-h-0 flex-1 list-none space-y-2.5 overflow-y-auto p-2.5"
          >
            {column.cards.map((card) => (
              <li
                key={card.id}
                draggable={(canMove && noDragCardId !== card.id) || undefined}
                /*
                 * Capture phase, so it runs before anything inside the card can
                 * stop the event — and the action block deliberately does not
                 * stop `pointerdown` anyway, because the Move menu closes on a
                 * document-level listener for it.
                 *
                 * Setting the id back to null on a press outside the action is
                 * what re-arms the drag, and React bails out of a state update
                 * that changes nothing, so an ordinary press on an ordinary
                 * card costs no render.
                 */
                onPointerDownCapture={
                  suppressible
                    ? (event) => {
                        const target = event.target;
                        setNoDragCardId(
                          target instanceof Element &&
                            target.closest('[data-kanban-no-drag]') !== null
                            ? card.id
                            : null,
                        );
                      }
                    : undefined
                }
                onPointerUp={suppressible ? () => setNoDragCardId(null) : undefined}
                onPointerCancel={suppressible ? () => setNoDragCardId(null) : undefined}
                onDragStart={(event) => onCardDragStart(event, card, column.id)}
                onDragEnd={onCardDragEnd}
                className={cx(
                  canMove && 'cursor-grab active:cursor-grabbing',
                  movingCardId === card.id && 'opacity-60',
                )}
              >
                <KanbanCard
                  card={card}
                  dateField={dateField}
                  href={hrefFor(card.id)}
                  targets={targetsForCard}
                  canMove={canMove && targetsForCard.length > 0}
                  moving={movingCardId === card.id}
                  showReference={showReference}
                  cardAction={cardAction}
                  {...(cardActionCount === undefined ? {} : { cardActionCount })}
                  columnName={column.name}
                  onMove={(toColumnId) => {
                    void onMove(card.id, column.id, toColumnId);
                  }}
                />
              </li>
            ))}
          </ul>
        )}

        {/*
         * ⛔ **THE FOOTER'S SENTENCE WAS REMOVED — owner's helper-text ruling,
         * 2026-09-05.** When there is nothing to link to it used to read
         * `— raise the cards per column to see more`.
         *
         * ⚠️ **The COUNT and the LINK stay, because neither is a sentence**: the
         * count is data, and "view all" is a control. What is gone is the
         * instruction for the case that HAS no control — `viewAllHref === null`,
         * which is the `id: null` "Unassigned" column, because "no stage" is not
         * a filter the list endpoint takes. **That column now says how many cards
         * are hidden and offers no way at all to reach them**; the only route is
         * the board-wide cards-per-column control, which is no longer named.
         */}
        {column.hasMore && (
          <footer className="border-t border-border bg-surface-sunken px-2.5 py-1.5 text-2xs text-fg-muted">
            <span className="tabular">
              Showing {column.cards.length} of {column.count}
            </span>
            {viewAllHref !== null && (
              <>
                {' '}
                &mdash;{' '}
                <TextLink to={viewAllHref} className="font-medium">
                  view all
                </TextLink>
              </>
            )}
          </footer>
        )}
      </section>
    </li>
  );
}

/*
 * The column total as the server now reports it: one figure per currency, in
 * the server's order (currency ascending, no-currency last). Each line is its
 * own `Money` — the entries are NEVER added together, because they are
 * different currencies and there is no rate to put them on one scale.
 *
 * An empty list is a money column with nothing in it, and renders exactly what
 * the old `totalAmount: "0.00"` did — a bare `0.00`, with no currency code it
 * was never given.
 */
function CurrencyTotals({ totals }: { totals: KanbanCurrencyTotal[] }) {
  if (totals.length === 0) {
    return (
      <p className="tabular m-0 text-xs font-medium text-fg-secondary" title="Total for the whole column">
        {formatDecimalString('0.00')}
      </p>
    );
  }

  return (
    <p className="m-0 grid gap-0.5 text-xs font-medium text-fg-secondary" title="Total for the whole column">
      {totals.map((entry) => (
        <Money key={entry.currency ?? 'none'} amount={entry.amount} currency={entry.currency} />
      ))}
    </p>
  );
}

/*
 * FALLBACK ONLY — used when the API omits `totalsByCurrency` (an older build).
 *
 * `totalAmount` sums ACROSS currencies. There is no base-currency column in the
 * schema and no currency filter on the board, so one figure over a mixed column
 * is wrong by construction rather than merely imprecise. When the loaded cards
 * disagree the count leads and the figure is demoted to an explicitly indicative
 * number with its codes spelled out. The DECIMAL string is never passed through
 * `Number()`.
 *
 * `totalAmount` is non-null HERE on purpose, though the contract types it
 * nullable: the caller decides whether there is any money to talk about and
 * simply does not render this when there isn't. Accepting the null would put a
 * "there is no total" branch inside the component whose entire job is to
 * present one, and would invite the em-dash `formatDecimalString` renders for a
 * null — a placeholder that says "this value is missing", which is the one
 * thing a money-less column must not claim.
 */
function ColumnTotal({
  totalAmount,
  mixed,
  codes,
  soleCode,
  partial,
}: {
  totalAmount: string;
  mixed: boolean;
  codes: string[];
  soleCode: string | undefined;
  partial: boolean;
}) {
  if (mixed) {
    const list = codes.join(', ');
    return (
      <p
        className="m-0 grid gap-0.5"
        title={`This column holds ${list}. A sum across them is indicative only.`}
      >
        <span className="tabular text-xs text-fg-subtle">
          &asymp; {formatDecimalString(totalAmount)}
        </span>
        {/*
         * The caveat is worded and adjacent, not a tooltip and not a tint: a
         * reader who never hovers must still not take this figure as fact.
         */}
        <span className="text-2xs text-fg-subtle">Indicative &mdash; mixed {list}</span>
      </p>
    );
  }

  return (
    <p
      className="tabular m-0 text-xs font-medium text-fg-secondary"
      /*
       * The codes are read off the loaded cards, so a truncated column could
       * still be hiding a second currency further down — say so rather than
       * stamping a code on a total that may not be in it.
       */
      title={
        partial && soleCode !== undefined
          ? `Total for the whole column. ${soleCode} is the currency of the cards loaded.`
          : 'Total for the whole column'
      }
    >
      {formatDecimalString(totalAmount)}
      {soleCode !== undefined && (
        <span className="ml-1 text-2xs font-normal text-fg-muted">{soleCode}</span>
      )}
    </p>
  );
}

/** Columns in the real shape — a spinner tells you nothing about what is coming. */
function BoardSkeleton() {
  return (
    <div className="min-w-0 overflow-x-auto pb-1" aria-hidden="true">
      <div className="flex items-start gap-3">
        {Array.from({ length: SKELETON_COLUMNS }, (_, columnIndex) => (
          <div key={columnIndex} className={cx(COLUMN_WIDTH, COLUMN_SHELL, 'border-border')}>
            <div className="grid gap-1 border-b border-border px-2.5 py-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-28 flex-1" />
                <Skeleton className="h-4 w-7 rounded-sm" />
              </div>
              <Skeleton className="h-3 w-20" />
            </div>
            {/* The real card's anatomy at the real card's size: two title
                lines, a company line, then the supporting block behind its
                rule. A loading board that is half the height of the board
                replacing it makes the whole page jump on arrival. */}
            <div className="grid gap-2.5 p-2.5">
              {Array.from({ length: SKELETON_CARDS }, (_, cardIndex) => (
                <div
                  key={cardIndex}
                  className="grid gap-3 rounded-lg border border-border bg-surface px-3.5 py-3"
                >
                  <div className="grid gap-1.5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="mt-0.5 h-3.5 w-1/2" />
                  </div>
                  <div className="grid gap-2 border-t border-border pt-2.5">
                    <Skeleton className="h-3.5 w-28" />
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
